import { createMachine, assign, setup } from "xstate";
import { ExemptionQuestions } from "utils/types/exemption-questions";
import { commonGuards } from "./_commonGuards";

export const californiaExemptionQuestionFlowMachine = setup({
	types: {
		context: {} as ExemptionQuestions.FlowContext,
		events: {} as ExemptionQuestions.FlowEvent,
		input: {} as Partial<ExemptionQuestions.FlowContext>,
	},
	actions: {
		updateContext: assign(({ context, event }) => {
			if ("context" in event) {
				return {
					...context,
					...event.context,
				};
			}
			return context;
		}),
	},
	guards: {
		noCurrentHomesteadExemption: ({ context, event }) =>
			[
				context.primaryResidenceValue === true,
				commonGuards.currentlyReceivingNoOrNotSure(context),
			].every(value => value === true),
	},
	delays: {},
}).createMachine({
	id: "CALIFORNIA_EXEMPTION",
	context: ({ input }) => ({ ...input }) as ExemptionQuestions.FlowContext,
	initial: "IDLE",
	states: {
		IDLE: {},
		INIT: {
			always: [
				{
					target: "PRIMARY_RESIDENCE",
					guard: ({ context, event }) =>
						context.primaryResidenceValue === undefined,
				},
				{
					target: "CURRENTLY_RECEIVING",
					guard: ({ context, event }) => {
						return context.currentlyReceivingValue === undefined;
					},
				},
				{
					target: "RARE_SITUATIONS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.hasDisability === true,
							context.isVeteran === true,
							context.isInheritedProperty === true,
							context.ownedThroughNonProfit === true,
							context.specialFieldsValue?.is_active_duty_personnel ===
								undefined,
							context.noneOfTheAbove === undefined,
						].some(value => value === true),
				},
				{
					target: "PURCHASE_DATE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.purchaseDateValue === undefined,
				},
				{
					target: "MOVE_IN_DATE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.moveInDateValue === undefined,
				},
				{
					target: "SSN_TIN",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.owner1HasEnteredSsnTin === undefined,
				},
				{
					target: "OWNERS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.ownersValue === undefined,
				},
				{
					target: "SSN_TIN2",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						["Own With Spouse", "Other Persons"].includes(
							context.ownersValue ?? ""
						) &&
						context.additionalOwnerFirstNameValue == undefined &&
						context.additionalOwnerLastNameValue === undefined &&
						context.owner2HasEnteredSsnTin === undefined,
				},
				{
					target: "AGREEMENT",
					guard: ({ context, event }) =>
						[
							context.primaryResidenceValue === true,
							commonGuards.currentlyReceivingNoOrNotSure(context),
						].every(value => value === true),
				},
			],
		},
		PRIMARY_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => (1 / 12) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "CURRENTLY_RECEIVING",
						guard: ({ context, event }) =>
							commonGuards.isPrimaryResidence(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							commonGuards.isNotPrimaryResidence(context),
					},
				],
			},
		},
		NON_AGREEMENT_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
		},
		CURRENTLY_RECEIVING: {
			entry: [
				assign({
					exemptionsProgress: context => (2 / 12) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "RARE_SITUATIONS",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
					},
				],
				back: {
					target: "PRIMARY_RESIDENCE",
					actions: assign({
						currentlyReceivingValue: context => undefined,
					}),
				},
			},
		},
		RARE_SITUATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (4 / 12) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							context.hasDisability === true ||
							context.isVeteran === true ||
							context.isInheritedProperty === true ||
							context.ownedThroughNonProfit === true,
					},
					{
						target: "PURCHASE_DATE",
						guard: ({ context, event }) =>
							commonGuards.rareSituationsAbleToFile(context),
					},
				],
				back: [
					{
						target: "CURRENTLY_RECEIVING",
						actions: assign({
							hasDisability: context => undefined,
							isVeteran: context => undefined,
							isInheritedProperty: context => undefined,
							ownedThroughNonProfit: context => undefined,
							noneOfTheAbove: context => undefined,
							specialFieldsValue: ({ context }) => ({
								...context.specialFieldsValue,
								is_active_duty_personnel: undefined,
							}),
						}),
					},
				],
			},
		},
		PURCHASE_DATE: {
			entry: [
				assign({
					exemptionsProgress: context => (5 / 12) * 100,
				}),
			],
			on: {
				next: {
					target: "MOVE_IN_DATE",
				},
				back: {
					target: "RARE_SITUATIONS",
					actions: assign({
						purchaseDateValue: context => undefined,
					}),
				},
			},
		},
		MOVE_IN_DATE: {
			entry: [
				assign({
					exemptionsProgress: context => (6 / 12) * 100,
				}),
			],
			on: {
				next: {
					target: "SSN_TIN",
				},
				back: {
					target: "PURCHASE_DATE",
					actions: assign({
						moveInDateValue: context => undefined,
						retroSavingsAmount: context => undefined,
						futureSavingsAmount: context => undefined,
					}),
				},
			},
		},
		SSN_TIN: {
			entry: [
				assign({
					exemptionsProgress: context => (7 / 12) * 100,
				}),
			],
			on: {
				next: {
					target: "OWNERS",
				},
				back: {
					target: "MOVE_IN_DATE",
					actions: assign({
						owner1SsnTin: context => undefined,
						owner1HasEnteredSsnTin: context => undefined,
					}),
				},
			},
		},
		OWNERS: {
			entry: [
				assign({
					exemptionsProgress: context => (8 / 12) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "AGREEMENT",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
					},
					{
						target: "SSN_TIN2",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
					},
				],
				back: {
					target: "SSN_TIN",
					actions: assign({ ownersValue: context => undefined }),
				},
			},
		},
		SSN_TIN2: {
			entry: [
				assign({
					exemptionsProgress: context => (9 / 12) * 100,
				}),
			],
			on: {
				next: {
					target: "AGREEMENT",
				},
				back: {
					target: "OWNERS",
					actions: assign({
						owner2SsnTin: context => undefined,
						owner2HasEnteredSsnTin: context => undefined,
						additionalOwnerFirstNameValue: context => undefined,
						additionalOwnerLastNameValue: context => undefined,
					}),
				},
			},
		},
		AGREEMENT: {
			entry: [
				assign({
					exemptionsProgress: context => (11 / 12) * 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
				back: [
					{
						target: "OWNERS",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
					},
					{
						target: "SSN_TIN2",
					},
				],
			},
		},
		MAIN_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
	},
	on: {
		update_context: {
			actions: "updateContext",
		},
		restart: {
			target: ".INIT",
		},
	},
});
